import { useCallback, useEffect, useMemo } from 'react';
import { useGetPenMovements } from './apiHooks';
import { useAppDispatch, useAppSelector } from '../../../../../../Redux/Store';
import {
    updateDistributionScenario,
    updateLotPenFeedDistributions,
    updatePenDetail,
} from '../../../../../../Redux/Reducer/manualFeedEntryReducer';
import {
    IExistingFeedEntryPenDetail,
    IFeedDistributionMovement,
    ILotPenFeedDistribution,
} from '../../../../../../utils/Interface/ManualFeedEntryInterface';
import { getApiFmByFlidReportsFeedlotFeedlotMovementsAndHdCountsApiResponse } from '../../../../../../Redux/Apis/Reports/reportsApi';
import dayjs from '../../../../../../utils/dayjs';
import {
    FeedDistributionMoveTypes,
    FeedDistributionScenarios,
} from '../interfaces';
import { ManualFeedEntryConstants } from '../../../../../../utils/constants/ManualFeedEntryConstants';

export const useGetFeedDistribution = (
    penDetail: IExistingFeedEntryPenDetail,
) => {
    const selectedDate = useAppSelector(
        state => state.manualFeedEntry.feedEntryRationDetails.date,
    );

    const { penMovements } = useGetPenMovements(penDetail.penId);

    const movementsByLot = useMemo(
        () =>
            penMovements?.reduce(
                (acc, movement) => {
                    if (!acc[movement.lotId]) acc[movement.lotId] = [];
                    acc[movement.lotId].push(movement);
                    return acc;
                },
                {} as Record<
                    number,
                    getApiFmByFlidReportsFeedlotFeedlotMovementsAndHdCountsApiResponse[]
                >,
            ),
        [penMovements],
    );

    const sortLotPenFeedDistributions = (
        lotPenFeedDistributionsToSort: ILotPenFeedDistribution[],
    ) => {
        return lotPenFeedDistributionsToSort.sort((a, b) =>
            a.lotName.localeCompare(b.lotName),
        );
    };

    const getEndOfDayHeadCount = useCallback(
        (
            movements: getApiFmByFlidReportsFeedlotFeedlotMovementsAndHdCountsApiResponse[],
        ): number => {
            const movementWithEndOfDayHeadCount = movements.reduce<
                | getApiFmByFlidReportsFeedlotFeedlotMovementsAndHdCountsApiResponse
                | undefined
            >((selected, current) => {
                if (selected) {
                    if (
                        dayjs(current.date).isSame(dayjs(selectedDate), 'day')
                    ) {
                        if (
                            !dayjs(selected.date).isSame(
                                dayjs(selectedDate),
                                'day',
                            )
                        ) {
                            return current;
                        } else {
                            return dayjs(current.timestamp).isAfter(
                                dayjs(selected.timestamp),
                            )
                                ? current
                                : selected;
                        }
                    } else {
                        return current;
                    }
                } else {
                    return current;
                }
            }, undefined);

            return movementWithEndOfDayHeadCount?.sublotHdCount ?? 0;
        },
        [selectedDate],
    );

    const getFedHeadCount = useCallback(
        (lotId: number, feedingNumber: number) => {
            return (
                penDetail.penSnapshot?.lotContexts?.findLast(
                    lotContext =>
                        lotContext.lotId === lotId &&
                        lotContext.feedingNumber === feedingNumber,
                )?.hdCount ?? 0
            );
        },
        [penDetail.penSnapshot?.lotContexts],
    );

    const getFeedDistributions = useCallback(() => {
        const feedDistributions: ILotPenFeedDistribution[] = [];

        if (movementsByLot && Object.keys(movementsByLot).length) {
            Object.entries(movementsByLot).forEach(([key, movements]) => {
                const lotId = +key;
                const eodHeadCount = getEndOfDayHeadCount(movements);
                feedDistributions.push({
                    lotId,
                    lotName: movements[0].lotName,
                    headCount: eodHeadCount,

                    feed1HeadCount: getFedHeadCount(lotId, 1),
                    feed2HeadCount: getFedHeadCount(lotId, 2),
                    feed3HeadCount: getFedHeadCount(lotId, 3),
                    feed4HeadCount: getFedHeadCount(lotId, 4),
                });
            });
        }

        return sortLotPenFeedDistributions(feedDistributions);
    }, [getEndOfDayHeadCount, getFedHeadCount, movementsByLot]);

    return getFeedDistributions;
};

const useSavedDistribution = (uiContext?: string) => {
    const savedDistribution: ILotPenFeedDistribution[] = useMemo(() => {
        return uiContext ? JSON.parse(uiContext) : undefined;
    }, [uiContext]);

    return savedDistribution;
};

export const usePopulateFeedDistributionTable = (
    penDetail: IExistingFeedEntryPenDetail,
) => {
    const dispatch = useAppDispatch();

    const selectedDate = useAppSelector(
        state => state.manualFeedEntry.feedEntryRationDetails.date,
    );
    const getFeedDistributions = useGetFeedDistribution(penDetail);
    const { penMovements } = useGetPenMovements(penDetail.penId);

    const savedDistribution = useSavedDistribution(penDetail.uiContext);

    const isMismatchBetweenSavedandActualFeedDistributions = (
        savedDistribution: ILotPenFeedDistribution[],
        actualFeedDistributions: ILotPenFeedDistribution[],
    ): boolean => {
        const savedDistributionString = JSON.stringify(savedDistribution);
        const actualFeedDistributionsString = JSON.stringify(
            actualFeedDistributions,
        );

        return savedDistributionString !== actualFeedDistributionsString;
    };

    useEffect(() => {
        if (!penDetail.lotPenFeedDistributions && penMovements) {
            const lotPenFeedDistributions = getFeedDistributions();
            let distributionScenario = FeedDistributionScenarios.NoCattle;
            const sameDayMovements = penMovements.filter(
                movement =>
                    dayjs(movement.date).isSame(dayjs(selectedDate), 'day') &&
                    movement.actionType !== FeedDistributionMoveTypes.Baseline,
            );
            if (savedDistribution) {
                if (
                    isMismatchBetweenSavedandActualFeedDistributions(
                        savedDistribution,
                        lotPenFeedDistributions,
                    )
                ) {
                    distributionScenario =
                        FeedDistributionScenarios.PreviouslySavedDistributionIsOutOfSync;
                } else {
                    distributionScenario =
                        FeedDistributionScenarios.HasPreviouslySavedDistribution;
                }
            } else if (lotPenFeedDistributions.length) {
                if (penDetail.estimatedHeadCount) {
                    distributionScenario =
                        FeedDistributionScenarios.HasEstimatedHead;
                } else if (sameDayMovements.length) {
                    distributionScenario =
                        FeedDistributionScenarios.HasNoEstimatedHead;
                } else {
                    distributionScenario =
                        FeedDistributionScenarios.NoMovements;
                }
            }
            dispatch(
                updateLotPenFeedDistributions({
                    penId: penDetail.penId,
                    distributionScenario,
                    lotPenFeedDistributions,
                }),
            );
        }
    }, [
        dispatch,
        getFeedDistributions,
        penDetail.estimatedHeadCount,
        penDetail.lotPenFeedDistributions,
        penDetail.penId,
        penMovements,
        savedDistribution,
        selectedDate,
    ]);
};

export const checkIsTableDisabled = (
    distributionScenario: FeedDistributionScenarios,
) => {
    return (
        ManualFeedEntryConstants.feedDistributionScenarios[distributionScenario]
            ?.isTableDisabled ?? true
    );
};

export const useGetFeedDistributionConfirmation = (
    penDetail: IExistingFeedEntryPenDetail,
) => {
    const dispatch = useAppDispatch();

    const getFeedDistributions = useGetFeedDistribution(penDetail);

    const message =
        ManualFeedEntryConstants.feedDistributionScenarios[
            penDetail.distributionScenario
        ]?.message ?? '';
    const buttonLabel =
        ManualFeedEntryConstants.feedDistributionScenarios[
            penDetail.distributionScenario
        ]?.buttonLabel ?? '';
    const isTableDisabled = checkIsTableDisabled(
        penDetail.distributionScenario,
    );

    const scenarioToggleMap = {
        [FeedDistributionScenarios.HasNoEstimatedHead]:
            FeedDistributionScenarios.CreatingDistributionWithNoEstimatedHead,

        [FeedDistributionScenarios.CreatingDistributionWithNoEstimatedHead]:
            FeedDistributionScenarios.HasNoEstimatedHead,
        [FeedDistributionScenarios.HasEstimatedHead]:
            FeedDistributionScenarios.CreatingDistributionWithEstimatedHead,

        [FeedDistributionScenarios.CreatingDistributionWithEstimatedHead]:
            FeedDistributionScenarios.HasEstimatedHead,
        [FeedDistributionScenarios.HasPreviouslySavedDistribution]:
            FeedDistributionScenarios.UpdatingDistribution,

        [FeedDistributionScenarios.UpdatingDistribution]:
            FeedDistributionScenarios.HasPreviouslySavedDistribution,
        [FeedDistributionScenarios.PreviouslySavedDistributionIsOutOfSync]:
            FeedDistributionScenarios.RevertToOutOfSync,

        [FeedDistributionScenarios.RevertToOutOfSync]:
            FeedDistributionScenarios.PreviouslySavedDistributionIsOutOfSync,
    };

    const onClick = () => {
        if (
            ManualFeedEntryConstants.revertToCurrentDistribution.includes(
                penDetail.distributionScenario,
            )
        ) {
            const feedDistributions = getFeedDistributions();
            dispatch(
                updateLotPenFeedDistributions({
                    penId: penDetail.penId,
                    distributionScenario:
                        scenarioToggleMap[penDetail.distributionScenario],
                    lotPenFeedDistributions: feedDistributions,
                }),
            );
        } else {
            dispatch(
                updateDistributionScenario({
                    penId: penDetail.penId,
                    distributionScenario:
                        scenarioToggleMap[penDetail.distributionScenario],
                }),
            );
        }
    };

    return {
        message,
        buttonLabel,
        isTableDisabled,
        onClick,
    };
};

export const useIsFetchingFeedDistributionData = (penId: number) => {
    const { isFetchingPenMovements } = useGetPenMovements(penId);

    return isFetchingPenMovements;
};

const mapPenFeedingPropertyToDistributionFeedingProperty = (
    penFeedingProperty: string,
) => {
    return penFeedingProperty.concat('HeadCount');
};

export const mapFeedingPropertyToErrorProperty = (feedingProperty: string) => {
    return feedingProperty
        .replace('HeadCount', '')
        .concat('DistributionHasError');
};

export const useIsFeedDistributionsValid = () => {
    const dispatch = useAppDispatch();

    const existingFeedEntryPensDetails = useAppSelector(
        state => state.manualFeedEntry.existingFeedEntryPensDetails,
    );

    const distributionForFeedingIsValid = (
        feedingNumber: string,
        feedingAmount: number,
        penDetail: IExistingFeedEntryPenDetail,
    ) => {
        let isValid = true;
        const penFeedDistributions = penDetail.lotPenFeedDistributions;
        const isTableDisabled = checkIsTableDisabled(
            penDetail.distributionScenario,
        );

        if (penFeedDistributions) {
            const feedingHasDistribution = penFeedDistributions.some(
                distribution => distribution[feedingNumber],
            );

            isValid =
                isTableDisabled || !feedingAmount || feedingHasDistribution;
        }

        return isValid;
    };

    const distributionsForPenAreValid = (
        penDetail: IExistingFeedEntryPenDetail,
    ) => {
        const penDetailCopy = { ...penDetail };

        let isValid = true;

        ['feed1', 'feed2', 'feed3', 'feed4'].forEach(feedKey => {
            const feedValue = penDetail[feedKey];
            if (
                !distributionForFeedingIsValid(
                    mapPenFeedingPropertyToDistributionFeedingProperty(feedKey),
                    feedValue,
                    penDetail,
                )
            ) {
                penDetailCopy[mapFeedingPropertyToErrorProperty(feedKey)] =
                    true;
                isValid = false;
            }
        });

        if (!isValid) {
            dispatch(updatePenDetail(penDetailCopy));
        }

        return isValid;
    };

    const isFeedDistributionsValid = () => {
        let isValid = true;

        existingFeedEntryPensDetails.forEach(penDetail => {
            if (!distributionsForPenAreValid(penDetail)) {
                isValid = false;
            }
        });

        return isValid;
    };

    return isFeedDistributionsValid;
};

export const useClearFeedDistributionValues = () => {
    const clearFeedDistributionValuesForSinglePen = (
        existingFeedEntryPensDetailsCopy: IExistingFeedEntryPenDetail,
    ) => {
        existingFeedEntryPensDetailsCopy.feed1DistributionHasError = undefined;
        existingFeedEntryPensDetailsCopy.feed2DistributionHasError = undefined;
        existingFeedEntryPensDetailsCopy.feed3DistributionHasError = undefined;
        existingFeedEntryPensDetailsCopy.feed4DistributionHasError = undefined;
        existingFeedEntryPensDetailsCopy.lotPenFeedDistributions = undefined;
        existingFeedEntryPensDetailsCopy.uiContext = undefined;
        existingFeedEntryPensDetailsCopy.showFeedDistribution = false;
        existingFeedEntryPensDetailsCopy.distributionScenario =
            FeedDistributionScenarios.NoCattle;
    };

    const clearFeedDistributionValuesForMultiplePens = (
        existingFeedEntryPensDetailsCopy: IExistingFeedEntryPenDetail[],
    ) => {
        existingFeedEntryPensDetailsCopy.forEach(
            clearFeedDistributionValuesForSinglePen,
        );
    };

    return {
        clearFeedDistributionValuesForSinglePen,
        clearFeedDistributionValuesForMultiplePens,
    };
};

export const useGetMovements = (penId: number) => {
    const selectedDate = useAppSelector(
        state => state.manualFeedEntry.feedEntryRationDetails.date,
    );

    const { penMovements } = useGetPenMovements(penId);

    const calculateMovement = (
        movement: getApiFmByFlidReportsFeedlotFeedlotMovementsAndHdCountsApiResponse,
    ): IFeedDistributionMovement => {
        return {
            actionType: FeedDistributionMoveTypes[movement.actionType],
            headcount: movement.hdCount,
            referencePenName: movement.refPens || '',
        };
    };

    const getTooltipMovements = (
        lotId: number,
    ): IFeedDistributionMovement[] => {
        const orderedMoveTypes = Object.values(FeedDistributionMoveTypes);

        return (
            penMovements
                ?.filter(
                    movement =>
                        movement.lotId === lotId &&
                        dayjs(movement.date).isSame(
                            dayjs(selectedDate),
                            'day',
                        ) &&
                        movement.actionType !==
                            FeedDistributionMoveTypes.Baseline,
                )
                ?.map(movement => {
                    return calculateMovement(movement);
                })
                ?.sort(
                    (a, b) =>
                        orderedMoveTypes.indexOf(
                            a.actionType as FeedDistributionMoveTypes,
                        ) -
                        orderedMoveTypes.indexOf(
                            b.actionType as FeedDistributionMoveTypes,
                        ),
                ) ?? []
        );
    };

    return getTooltipMovements;
};
